import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const Footer = () => {
  const [isPolicyPopupOpen, setIsPolicyPopupOpen] = useState(false);
  const [selectedPolicyKey, setSelectedPolicyKey] = useState('terms');
  const [selectedVersion, setSelectedVersion] = useState('v1.1');
  const [policyHtml, setPolicyHtml] = useState('');
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyFailed, setPolicyFailed] = useState(false);
  const abortRef = useRef(null);

  const policyMap = useMemo(() => ({
    terms: {
      title: '이용약관',
      versions: [
        // { value: 'v1.1', label: 'v1.1 (2026-07-01)', file: '/policies/terms-v1.1.htm' },
        // { value: 'v1.0', label: 'v1.0 (2026-01-01)', file: '/policies/terms-v1.0.htm' }
      ]
    },
    privacy: {
      title: '개인정보처리방침',
      versions: [
        // { value: 'v1.1', label: 'v1.1 (2026-07-01)', file: '/policies/privacy-v1.1.htm' },
        { value: 'v1.0', label: 'v1.0 (2026-07-08)', file: '/policies/privacy-v1.0.htm' }
      ]
    },
    location: {
      title: '위치기반 서비스 이용약관',
      versions: [
        // { value: 'v1.1', label: 'v1.1 (2026-07-01)', file: '/policies/location-v1.1.htm' },
        // { value: 'v1.0', label: 'v1.0 (2026-01-01)', file: '/policies/location-v1.0.htm' }
      ]
    }
  }), []);

  const currentPolicy = policyMap[selectedPolicyKey];
  const currentVersionData = currentPolicy.versions.find((v) => v.value === selectedVersion) || currentPolicy.versions[0];

  const loadPolicyFile = useCallback(async (file) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setPolicyLoading(true);
    setPolicyFailed(false);
    setPolicyHtml('');

    try {
      const response = await fetch(file, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const html = await response.text();
      if (html.toLowerCase().includes('<div id="root"></div>')) {
        throw new Error('SPA fallback');
      }
      setPolicyHtml(html);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setPolicyFailed(true);
      }
    } finally {
      if (!controller.signal.aborted) {
        setPolicyLoading(false);
      }
    }
  }, []);

  const openPolicyPopup = useCallback((policyKey) => {
    const targetPolicy = policyMap[policyKey];
    if (!targetPolicy || targetPolicy.versions.length === 0) {
      return;
    }
    const firstVersion = targetPolicy.versions[0];
    setSelectedPolicyKey(policyKey);
    setSelectedVersion(firstVersion.value);
    setIsPolicyPopupOpen(true);
    loadPolicyFile(firstVersion.file);
  }, [policyMap, loadPolicyFile]);

  const closePolicyPopup = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    setIsPolicyPopupOpen(false);
  }, []);

  const handleVersionChange = useCallback((event) => {
    const version = event.target.value;
    setSelectedVersion(version);
    const policy = policyMap[selectedPolicyKey];
    const versionData = policy?.versions.find((v) => v.value === version);
    if (versionData) {
      loadPolicyFile(versionData.file);
    }
  }, [policyMap, selectedPolicyKey, loadPolicyFile]);

  useEffect(() => {
    if (!isPolicyPopupOpen) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePolicyPopup();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isPolicyPopupOpen, closePolicyPopup]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <footer id="footer">
        <button className="btn_top" onClick={scrollToTop}><img src="/assets/images/footer_btn_top.png" alt="위로 아이콘"/></button>
        <div className="footer">
          <div className="container footer_inner">
            <div className="footer_wrap" style={{ alignItems: 'center' }}>
              <div className="logo" style={{ display: 'flex', alignItems: 'center' }}>
                <a href="/">
                  <img src="/assets/images/logo_w.png" alt="드림티원 로고"/>
                </a>
              </div>
              <div className="footer_box">
                <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => openPolicyPopup('terms')}
                    disabled={policyMap.terms.versions.length === 0}
                    style={{ border: 'none', background: 'none', padding: 0, color: '#e5e7eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}
                  >
                    이용약관
                  </button>
                  <span style={{ color: '#9ca3af' }}>|</span>
                  <button
                    type="button"
                    onClick={() => openPolicyPopup('privacy')}
                    style={{ border: 'none', background: 'none', padding: 0, color: '#e5e7eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}
                  >
                    개인정보처리방침
                  </button>
                  <span style={{ color: '#9ca3af' }}>|</span>
                  <button
                    type="button"
                    onClick={() => openPolicyPopup('location')}
                    disabled={policyMap.location.versions.length === 0}
                    style={{ border: 'none', background: 'none', padding: 0, color: '#e5e7eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}
                  >
                    위치기반 서비스 이용약관
                  </button>
                </div>
                <div className="footer_adr">
                  <p>(주)드림티원 </p>
                  <p>서울 성동구 성수일로 55(SK테크노빌딩) 606호, 사업자번호: 333-86-03084</p>
                  <p>Tel. 02-6013-9638 Fax 070-5150-9639 대표자: 박기준</p>
                  <p>Copyright ⓒ Dream T1 Co.,Ltd. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div> 
        </div>
      </footer>

      {isPolicyPopupOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${currentPolicy.title} 문서 보기`}
          onClick={closePolicyPopup}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '18px'
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '980px',
              height: '82vh',
              backgroundColor: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '17px', color: '#111827' }}>{currentPolicy.title}</strong>
                <label htmlFor="policy-version" style={{ fontSize: '13px', color: '#4b5563' }}>버전 선택</label>
                <select
                  id="policy-version"
                  value={selectedVersion}
                  onChange={handleVersionChange}
                  style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px', fontSize: '14px' }}
                >
                  {currentPolicy.versions.map((version) => (
                    <option key={version.value} value={version.value}>{version.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={closePolicyPopup}
                style={{ border: 'none', background: '#111827', color: '#fff', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px' }}
              >
                닫기
              </button>
            </div>

            {policyLoading && (
              <div style={{ padding: '24px', color: '#374151', fontSize: '14px' }}>
                정책 문서를 불러오는 중입니다...
              </div>
            )}

            {!policyLoading && !policyFailed && policyHtml && (
              <iframe
                key={currentVersionData?.value}
                srcDoc={policyHtml}
                title={`${currentPolicy.title} ${currentVersionData?.label ?? ''}`}
                style={{ width: '100%', flex: 1, border: 'none', backgroundColor: '#fff' }}
              />
            )}

            {!policyLoading && policyFailed && (
              <div style={{ padding: '24px', color: '#374151', fontSize: '14px' }}>
                정책 문서를 불러오지 못했습니다.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;

